import { useEffect } from 'react';
import { Question } from '../types/game';
import { QuestionResult } from '../types/game';
import { TIMER_UPDATE_INTERVAL } from '../constants/gameConstants';

interface UseQuestionTimerProps {
  currentQuestion: Question | null;
  timeRemaining: number;
  questionResult: QuestionResult | null;
  gameState: 'waiting' | 'playing' | 'finished';
  roomHasCurrentQuestion: boolean;
  isPaused?: boolean;
  setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
}

interface UseQuestionTimerReturn {
  timeSeconds: number;
  progress: number;
}

/**
 * Hook to manage question timer countdown
 */
export const useQuestionTimer = ({
  currentQuestion,
  timeRemaining,
  questionResult,
  gameState,
  roomHasCurrentQuestion,
  isPaused = false,
  setTimeRemaining,
}: UseQuestionTimerProps): UseQuestionTimerReturn => {
  // Timer countdown - only runs when we have a question, time remaining > 0, no result shown, and game is playing
  useEffect(() => {
    // CRITICAL: Only run timer if:
    // 1. We have a current question
    // 2. Time remaining > 0
    // 3. No question result is shown
    // 4. Game state is 'playing'
    // 5. Room state has a current question (not cleared)
    // 6. Game is not paused
    if (
      currentQuestion && 
      timeRemaining > 0 && 
      !questionResult && 
      gameState === 'playing' &&
      roomHasCurrentQuestion && // Question not cleared by backend
      !isPaused
    ) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= TIMER_UPDATE_INTERVAL) {
            return 0; // Timer reached 0 - backend will advance question
          }
          return prev - TIMER_UPDATE_INTERVAL;
        });
      }, TIMER_UPDATE_INTERVAL);

      return () => clearInterval(timer);
    } else if (timeRemaining > 0 && (!currentQuestion || questionResult || gameState !== 'playing' || !roomHasCurrentQuestion)) {
      // Stop timer if conditions are not met
      setTimeRemaining(0);
    }
  }, [currentQuestion, timeRemaining, questionResult, gameState, roomHasCurrentQuestion, isPaused, setTimeRemaining]);

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = currentQuestion ? (timeRemaining / currentQuestion.timeLimit) * 100 : 0;

  return {
    timeSeconds,
    progress,
  };
};
